import type { ReactNode } from 'react'
import { useState } from 'react'
import { useIntl } from 'react-intl'

import { cn } from '@gto/ui/cn'
import type {
  DataTableProps as DsDataTableProps,
  OnChangeFn,
  PaginationState,
} from '@gto/ui/data-table'
import { DataTable as DsDataTable, TablePager } from '@gto/ui/data-table'
import { Paper } from '@gto/ui/paper'

import { TABLE_SURFACE } from './table-surface'

/**
 * The old wrapper capped its scroller at `70dvh` so a long history page kept its footer
 * controls on screen. A LITERAL class, not a prop-driven `max-h-[${…}]`: Tailwind only
 * compiles classes it can see as literals, so an interpolated arbitrary value silently
 * produces no CSS at all.
 */
const SCROLL_CAP_CLASS = 'max-h-[70dvh] min-h-0'

/**
 * Header labels matched to the ROWS and set apart by weight, not size.
 *
 * The DS header is `body3` (12px, muted) — SMALLER than the 14px `body2` body cells under it, which
 * reads as broken once the rows carry real content. The old DataGridPro tables here went the other
 * way, much larger and bold, which made them top-heavy. So: same 14px as the rows, semibold, in
 * primary text. This is the one place rakeback deliberately reads differently from the affiliates
 * portal, which keeps the DS default — it is a consumer site.
 *
 * `px` on purpose (the repo pins font sizes so text does not scale with the root on 4k), and a
 * descendant selector because the DS exposes no header-typography prop — its `className` lands on
 * the outer wrapper. It also makes the row UNIFORM: the old headers rendered hint-carrying columns
 * through a different component, so "Edge multiplikator" and "Pripisan Edge" came out muted while
 * their neighbours were bold white.
 */
const HEADER_TEXT_CLASS = [
  '[&_[role=columnheader]]:text-text-primary',
  '[&_[role=columnheader]]:text-[14px]',
  '[&_[role=columnheader]]:font-semibold',
  // The DS truncates a header label to ONE line (`min-w-0 flex-1 truncate`), which at 14px
  // ellipsises the long ones ("Rake (bruto igralni prihodek / GGR)"). Two lines fit the 56px
  // header row exactly (2 x 20px) and recover most of what the old auto-height header showed.
  '[&_[role=columnheader]>span]:whitespace-normal',
  '[&_[role=columnheader]>span]:line-clamp-2',
].join(' ')

export type DataTableProps<R extends object> = Omit<
  DsDataTableProps<R>,
  // `children` mounts the pager; `query` is derived from `loading` + `refetching`;
  // `onPaginationChange` is re-typed to value-based below.
  | 'children'
  | 'query'
  | 'onPaginationChange'
  // Off for every table here — see the render site.
  | 'showColumnMenu'
  | 'showColumnVisibilityMenu'
> & {
  /** FIRST load — no rows yet. Drives the DS skeleton (`isPending`). */
  loading?: boolean
  /**
   * ANY in-flight fetch over rows already on screen — a `keepPreviousData` window or a plain
   * same-key background refetch. Kept apart from `loading`: the DS holds those rows under an
   * overlay instead of replacing them with a skeleton. Derive both with `tableQueryState`.
   */
  refetching?: boolean
  /**
   * The query REJECTED. Drives the DS error overlay and its retry, so a failed fetch does not
   * render as the empty state — "no data" and "we could not load it" are different answers.
   */
  isError?: boolean
  /** The error itself, kept inspectable rather than swallowed. */
  error?: unknown
  /** Re-runs the query behind the error overlay's retry button. */
  onRetry?: () => void
  /**
   * Total matching rows. REQUIRED alongside `manualPagination` — the engine only holds the
   * page it was handed, so nothing else can size the pager.
   */
  rowCount?: number
  /**
   * Rows-per-page choices. Omit entirely for a table that shows every row it is given: the
   * pagination engine then stays disengaged rather than riding a sentinel page size.
   */
  pageSizeOptions?: number[]
  onPaginationChange?: (next: PaginationState) => void
  /** Rendered inside the card below the rows — a totals strip, not the pager. */
  footer?: ReactNode
  /**
   * Let the card grow to its content instead of scrolling past 70dvh. For a list already
   * bounded by its own data (the rake race leaderboard is as long as its prize ladder).
   */
  uncapped?: boolean
  /** Extra classes for the card — e.g. a floor height for a table that would collapse. */
  className?: string
}

/**
 * The app's one table: the DS `DataTable` (TanStack) plus the house card, the footer page control,
 * and the chrome every rakeback table shares.
 *
 * Columns are TanStack `ColumnDef`s built with `createColumns<Row>()`; widths come from
 * `fluidColumn`/`fixedColumn` (`./table-columns`).
 *
 * CHROME IS DELIBERATELY NOT THE DS DEFAULT, and differs from the affiliates portal on purpose —
 * rakeback is a consumer site. The old tables layered card `elevation2` / rows `elevation3` /
 * header `elevation6`; reproduced here as `surface={TABLE_SURFACE}` (elevation3) plus
 * `headerSurface="elevation5"`, the DS scale stopping at 5. Header labels take the ROW size in
 * semibold white (`HEADER_TEXT_CLASS`) rather than the DS's smaller muted `body3`. `embedded`
 * stays because the `Paper` below owns the card silhouette — two rounded clips at different radii
 * carve a notch out of the inner corners.
 *
 * Density is the DS default; exactly two tables opt up to `comfortable`, each for a measured
 * reason (see the README).
 *
 * Sorting is OFF for every table — the old wrapper set `disableColumnSorting` globally and no call
 * site asked for it, so it is disabled once at the engine rather than per column.
 */
export const DataTable = <R extends object>({
  loading,
  refetching,
  isError,
  error,
  onRetry,
  rowCount,
  pageSizeOptions,
  pagination,
  onPaginationChange,
  footer,
  uncapped = false,
  className,
  density,
  tableOptions,
  ...props
}: DataTableProps<R>) => {
  const { formatMessage } = useIntl()

  // A paging table that does not control `pagination` rides this; one with no
  // `pageSizeOptions` never engages the engine at all, so `activePagination` stays undefined
  // and the DS renders every row it was given.
  const [ownPagination, setOwnPagination] = useState<PaginationState>(() => ({
    pageIndex: 0,
    pageSize: pageSizeOptions?.at(0) ?? 0,
  }))
  const isPaginationControlled = pagination !== undefined && onPaginationChange !== undefined
  const uncontrolledPagination = pageSizeOptions === undefined ? undefined : ownPagination
  const activePagination = isPaginationControlled ? pagination : uncontrolledPagination

  const emitPagination = (next: PaginationState) => {
    if (isPaginationControlled) {
      onPaginationChange(next)
    } else {
      setOwnPagination(next)
    }
  }

  // TanStack hands `onXChange` an UPDATER FUNCTION, not a value; resolved here once so call
  // sites receive a plain `PaginationState`.
  const handlePaginationChange: OnChangeFn<PaginationState> = (updaterOrValue) => {
    if (activePagination === undefined) {
      return
    }
    emitPagination(
      typeof updaterOrValue === 'function' ? updaterOrValue(activePagination) : updaterOrValue,
    )
  }

  // Localised overlay copy, defaulted here rather than per page: the DS's own fallbacks are
  // hardcoded English ("No data", "Something went wrong") and this app ships 14 locales. A page
  // with better wording still overrides either through `{...props}`.
  const emptyText = formatMessage({ id: 'common.table.empty' })
  const errorText = formatMessage({ id: 'common.table.error' })
  const retryText = formatMessage({ id: 'common.table.retry' })
  const stateCopy = {
    emptyMessage: emptyText,
    errorMessage: errorText,
    retryLabel: retryText,
    loadMoreErrorMessage: errorText,
    loadMoreRetryLabel: retryText,
  }
  const query = {
    isPending: loading,
    placeholderData: refetching,
    isError,
    error,
    refetch: onRetry,
  }
  const announcements = {
    empty: emptyText,
    error: errorText,
    loaded: () => formatMessage({ id: 'common.table.loaded' }),
  }

  const total = rowCount ?? props.data?.length ?? 0
  // The pager exists when the rows could overflow a page at ALL. Measured against the SMALLEST
  // offered size rather than the active one: the active size is set BY this control, so measuring
  // against it lets the control delete itself (30 rows, pick 100, no way back).
  //
  // It also stays while the reader is off page one, even if the rows now fit: a filter that
  // shrinks the total would otherwise unmount the pager while the query still asks for page N,
  // leaving an empty table and no control to get back.
  const fitsOnePage =
    activePagination === undefined ||
    total <= Math.min(...(pageSizeOptions ?? []), activePagination.pageSize)
  const showPager =
    pageSizeOptions !== undefined &&
    activePagination !== undefined &&
    (!fitsOnePage || activePagination.pageIndex > 0)

  return (
    // ONE card holding the rows AND the footer controls, as the old wrapper did. The table is
    // `embedded` BECAUSE of this Paper — two rounded clips at different radii carve a notch out
    // of the inner corners, so the silhouette belongs to the Paper alone.
    <Paper
      className={cn('flex flex-col overflow-hidden rounded-md', className)}
      data-tst="data-table-card"
      elevation="2"
    >
      <DsDataTable<R>
        embedded
        announcements={announcements}
        className={cn(HEADER_TEXT_CLASS, !uncapped && SCROLL_CAP_CLASS)}
        density={density}
        // The old tables layered card `elevation2` / rows `elevation3` / header `elevation6`, so the
        // header read as a distinctly lighter band rather than melting into the rows. Reproduced as
        // closely as the DS allows: its surface scale stops at `elevation5`.
        headerSurface="elevation5"
        pagination={activePagination}
        query={query}
        stateCopy={stateCopy}
        surface={TABLE_SURFACE}
        tableOptions={{ enableSorting: false, ...tableOptions }}
        onPaginationChange={handlePaginationChange}
        {...props}
        // AFTER the spread and omitted from this wrapper's props: both of DataGridPro's menus
        // stay off, so no call site can re-enable them by accident.
        // `showColumnVisibilityMenu` is `disableColumnSelector`, `showColumnMenu` the
        // per-column three-dot menu.
        showColumnMenu={false}
        showColumnVisibilityMenu={false}
      />

      {showPager && (
        <TablePager
          className="bg-gradient-paper1"
          pageIndex={activePagination.pageIndex}
          pageSize={activePagination.pageSize}
          pageSizeOptions={pageSizeOptions}
          total={total}
          labels={{
            nextPageAriaLabel: formatMessage({ id: 'common.table.nextPage' }),
            previousPageAriaLabel: formatMessage({ id: 'common.table.previousPage' }),
            renderRange: ({ from, to, total: rangeTotal }) =>
              formatMessage({ id: 'common.table.pageRange' }, { from, to, total: rangeTotal }),
            rowsPerPage: formatMessage({ id: 'common.table.rowsPerPage' }),
            rowsPerPageAriaLabel: formatMessage({ id: 'common.table.rowsPerPage' }),
          }}
          onPageChange={(pageIndex) => {
            emitPagination({ ...activePagination, pageIndex })
          }}
          onPageSizeChange={(pageSize) => {
            // Back to the first page: a stale index can land past the new last page.
            emitPagination({ pageIndex: 0, pageSize })
          }}
        />
      )}

      {footer}
    </Paper>
  )
}
